/**
 * Сборка XLSX маршрутного листа через ExcelJS (без Reportify).
 * Группировка сделок/продуктов совпадает с reportify/routes/generate.js (deals_group, product_group).
 */

import ExcelJS from "exceljs";
import { estimateRowHeightPt } from "@/modules/DeliverySheetBoard/estimateExcelRowHeight";

const LOG_PREFIX = "[routingSheetExcel]";

/** Увеличенный шрифт для печатной выгрузки (базовый ExcelJS ≈11). */
const FONT_SIZE = 12;
const FONT_SIZE_HEADER = 13;

/** Высота строк (pt) под увеличенный шрифт (+6 pt к базовым значениям для печати). */
const ROW_HEIGHT_META = 30;
const ROW_HEIGHT_TABLE_HEADER = 32;
const ROW_HEIGHT_DATA = 30;
const ROW_HEIGHT_TAKE_MIN = 34;

/**
 * Ширина столбцов (единицы ExcelJS, ~символы стандартного шрифта).
 * Увеличены под FONT_SIZE 12 и длинные наименования.
 */
const COLW_META_LABEL = 28;
const COLW_META_VALUE = 26;
const COLW_PAIR = 14;
const COLW_NUM = 7;
const COLW_MAIN = 24;
const COLW_PRODUCT = 38;
const COLW_NOTE = 36;
const COLW_REST = 12;
const COLW_WH_INDEX = 7;
const COLW_WH_CODE = 14;
const COLW_WH_NAME = 38;
const COLW_WH_PLACE = 26;
const COLW_WH_QTY = 11;
const COLW_WH_NOTE = 42;

/** Шапка маршрута: подписи в D, значения в E. */
const META_LABEL_COL = 4;
const META_VALUE_COL = 5;

function debug(...args: unknown[]) {
  if (process.env.NODE_ENV === "development") {
    console.debug(LOG_PREFIX, ...args);
  }
}

/** Достаёт скаляр из поля DocAPI JSON. */
export function coerceField(doc: any, key: string): any {
  if (!doc || doc[key] === undefined || doc[key] === null) {
    return "";
  }
  const v = doc[key];
  if (typeof v === "object") {
    if ("value" in v && (v as { value?: unknown }).value !== undefined) {
      return (v as { value: unknown }).value;
    }
    if ("pk" in v) {
      return (v as { pk: unknown }).pk;
    }
    if ("name" in v) {
      return (v as { name: unknown }).name;
    }
  }
  return v;
}

function strField(doc: any, key: string): string {
  const v = coerceField(doc, key);
  return v === null || v === undefined ? "" : String(v);
}

function numField(doc: any, key: string): number {
  const v = coerceField(doc, key);
  if (v === "" || v === null || v === undefined) {
    return 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function boolField(doc: any, key: string): boolean {
  const v = coerceField(doc, key);
  if (typeof v === "boolean") {
    return v;
  }
  if (typeof v === "number") {
    return v !== 0 && !Number.isNaN(v);
  }
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "false" || s === "0" || s === "") {
      return false;
    }
    if (s === "true" || s === "1" || s === "yes") {
      return true;
    }
  }
  return !!v;
}

/** Как deals_group в reportify (generate.js).
 *  special_orders=true → ключ дополняется deal.id (заявки не схлапываются). */
export function dealsGroup(deals: any[]): any[] {
  const truthy = (v: any) => v === true || v === "true" || v === 1 || v === "1" || v === "t";

  const groupKey = (deal: any, withAddress: boolean) => {
    const clientName = strField(deal, "client_name");
    const typeId = strField(deal, "type_id");
    const pointAddress = withAddress ? strField(deal, "point_address") : "";
    const base = clientName + typeId + pointAddress;
    if (truthy(deal.special_orders) || truthy(deal.client_special_orders)) {
      return base + strField(deal, "id");
    }
    return base;
  };

  const counts: Record<string, number> = {};
  let specialSplit = 0;
  deals.forEach((deal) => {
    const k1 = groupKey(deal, false);
    const k2 = groupKey(deal, true);
    counts[k1] = (counts[k1] || 0) + 1;
    counts[k2] = (counts[k2] || 0) + 1;
    if (truthy(deal.special_orders) || truthy(deal.client_special_orders)) {
      specialSplit += 1;
    }
    if (process.env.NODE_ENV === "development") {
      console.debug("[deals-group] key", {
        dealId: strField(deal, "id"),
        specialOrders: !!(
          truthy(deal.special_orders) || truthy(deal.client_special_orders)
        ),
        groupKey: k2
      });
    }
  });
  if (specialSplit > 0) {
    console.info("[deals-group] special_orders_split", { count: specialSplit });
  }

  const sorted = [...deals].sort((a, b) => {
    const sa = groupKey(a, true);
    const sb = groupKey(b, true);
    return sa.localeCompare(sb);
  });

  let prevName: string | null = null;
  let prevAddress: string | null = null;

  return sorted.map((deal) => {
    const gk = groupKey(deal, false);
    const ak = groupKey(deal, true);
    const row = { ...deal };
    row.first = gk !== prevName;
    row.counts = counts[gk];
    prevName = gk;
    row.first_address = ak !== prevAddress;
    row.counts_address = counts[ak];
    prevAddress = ak;
    return row;
  });
}

/** Как product_group в reportify. */
export function productGroup(products: any[]): any[] {
  const sorted = [...products].sort((a, b) =>
    strField(a, "product_name").localeCompare(strField(b, "product_name")),
  );
  let prevName: string | null = null;
  return sorted.map((product) => {
    const row = { ...product };
    const containers = row.containers || [];
    if (containers.length > 0) {
      containers[0] = {
        ...containers[0],
        first: strField(row, "product_name") !== prevName,
      };
      row.counts = containers.length;
    }
    prevName = strField(row, "product_name");
    return row;
  });
}

export type RoutingSheetBatchPayload = Record<
  string,
  { documents?: any[]; schema?: unknown; meta?: unknown }
>;

/** Один лист: заявки сверху, через 3 строки — продукты со склада. */
const SHEET_ROUTING = "Маршрутный лист";

/** Пустые строки между окончанием блока заявок и заголовком таблицы склада. */
const ROW_GAP_BEFORE_WAREHOUSE = 3;

function formatDeliveryDate(raw: unknown): string {
  const s = raw === null || raw === undefined ? "" : String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}.${m}.${y}`;
  }
  return s;
}

function thinBorder(): Partial<ExcelJS.Borders> {
  return {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
}

function clearCellBorders(cell: ExcelJS.Cell) {
  cell.border = {} as ExcelJS.Borders;
}

const BORDER_BLACK_THIN: Partial<ExcelJS.Border> = {
  style: "thin",
  color: { argb: "FF000000" },
};
const BORDER_BLACK_THICK: Partial<ExcelJS.Border> = {
  style: "thick",
  color: { argb: "FF000000" },
};

/** Верхняя строка шапки таблицы склада (персик), как в печатной форме. */
const WH_FILL_INFO_ROW: ExcelJS.Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFF4B084" },
};

/** Заголовки колонок продукт/тара/кол-ва (зелёный). */
const WH_FILL_COL_HEADER_GREEN: ExcelJS.Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF93C47D" },
};

/** Высота строки «Дата / Направление / Комплектовщик». */
const ROW_HEIGHT_WH_INFO = 36;

/** Колонки таблицы заявок: 1 ФИО, 2 Время, 3 №, 4–6 клиент, 7 продукт, 8 тара, 9–10 кол-ва, 11 примеч. */
const COL_LAST = 11;

function fillDealsTable(
  sheet: ExcelJS.Worksheet,
  startRow: number,
  deals: any[],
  dealsSum: any[],
  clientIndexMap: Map<string, number>,
  nextClientNo: { n: number },
): number {
  let r = startRow;
  const headerFill: ExcelJS.Fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF72BF44" },
  };

  const headers = [
    "ФИО",
    "Время",
    "№",
    "Клиент",
    "Адрес доставки",
    "Комментарий к адресу",
    "Наименование продукта",
    "Тип тары",
    "Количество ведер",
    "Количество кг",
    "Примечания",
  ];
  headers.forEach((h, c) => {
    const cell = sheet.getCell(r, c + 1);
    cell.value = h;
    cell.font = { bold: true, size: FONT_SIZE_HEADER };
    cell.fill = headerFill;
    cell.border = thinBorder();
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
  });
  sheet.getRow(r).height = ROW_HEIGHT_TABLE_HEADER;
  r += 1;

  const grouped = dealsGroup(deals);

  for (const deal of grouped) {
    const take = boolField(deal, "take");
    const firstAddr = !!deal.first_address;
    const rowspan = Number(deal.counts_address) || 1;

    const addrKey = `${strField(deal, "client_name")}|${strField(
      deal,
      "point_address",
    )}`;
    if (firstAddr && !clientIndexMap.has(addrKey)) {
      clientIndexMap.set(addrKey, nextClientNo.n);
      nextClientNo.n += 1;
    }
    const clientNo = clientIndexMap.get(addrKey) ?? "";

    if (take) {
      const row = sheet.getRow(r);
      const rs = Math.max(1, rowspan);
      /** Как в Reportify: «забрать» — только колонки продукта…примечание; 1–6 как у обычной строки адреса. */
      if (firstAddr) {
        for (let c = 1; c <= 6; c++) {
          sheet.mergeCells(r, c, r + rs - 1, c);
        }
        sheet.getCell(r, 1).value = "";
        sheet.getCell(r, 2).value = "";
        sheet.getCell(r, 3).value = clientNo;
        sheet.getCell(r, 4).value = strField(deal, "client_name");
        sheet.getCell(r, 5).value = strField(deal, "point_address");
        sheet.getCell(r, 6).value = strField(deal, "point_comment");
        for (let c = 1; c <= 6; c++) {
          const cell = sheet.getCell(r, c);
          cell.border = thinBorder();
          cell.font = { size: FONT_SIZE };
          if (c === 3) {
            cell.alignment = {
              vertical: "middle",
              horizontal: "center",
              wrapText: true,
            };
          } else {
            cell.alignment = { vertical: "top", wrapText: true };
          }
          const color = strField(deal, "color");
          if (color && color.startsWith("#")) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: `FF${color.slice(1)}` },
            };
          }
        }
      }

      sheet.mergeCells(r, 7, r, COL_LAST);
      const takeCell = sheet.getCell(r, 7);
      takeCell.value = strField(deal, "notes");
      takeCell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      takeCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFCCCCCC" },
      };
      takeCell.font = { color: { argb: "FF770000" }, bold: true, size: FONT_SIZE };
      takeCell.border = thinBorder();
      const takeNotes = strField(deal, "notes");
      const takeWidth =
        COLW_PRODUCT + COLW_REST * 3 + COLW_NOTE;
      row.height = estimateRowHeightPt({
        cells: [
          { text: takeNotes, colWidth: takeWidth },
          ...(firstAddr
            ? [
                { text: strField(deal, "point_address"), colWidth: COLW_MAIN },
                { text: strField(deal, "point_comment"), colWidth: COLW_MAIN },
                { text: strField(deal, "client_name"), colWidth: COLW_MAIN },
              ]
            : []),
        ],
        fontSize: FONT_SIZE,
        minHeight: ROW_HEIGHT_TAKE_MIN,
      });
      if (process.env.NODE_ENV === "development") {
        console.debug("[FIX:routingSheetExcel] take-row merged cols 7..COL_LAST", {
          row: r,
          firstAddr,
          rs,
          height: row.height,
          notesLen: takeNotes.length,
        });
      }
      r += 1;
      continue;
    }

    /** Все сделки кроме type «забрать»; если флаги не пришли из API — не терять строку. */
    if (!take) {
      if (firstAddr) {
        const rs = Math.max(1, rowspan);
        for (let c = 1; c <= 6; c++) {
          sheet.mergeCells(r, c, r + rs - 1, c);
        }
        sheet.getCell(r, 1).value = "";
        sheet.getCell(r, 2).value = "";
        sheet.getCell(r, 3).value = clientNo;
        sheet.getCell(r, 4).value = strField(deal, "client_name");
        sheet.getCell(r, 5).value = strField(deal, "point_address");
        sheet.getCell(r, 6).value = strField(deal, "point_comment");
        for (let c = 1; c <= 6; c++) {
          const cell = sheet.getCell(r, c);
          cell.border = thinBorder();
          cell.font = { size: FONT_SIZE };
          if (c === 3) {
            cell.alignment = {
              vertical: "middle",
              horizontal: "center",
              wrapText: true,
            };
          } else {
            cell.alignment = { vertical: "top", wrapText: true };
          }
          const color = strField(deal, "color");
          if (color && color.startsWith("#")) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: `FF${color.slice(1)}` },
            };
          }
        }
      }
      sheet.getCell(r, 7).value = strField(deal, "product_name");
      sheet.getCell(r, 8).value = strField(deal, "product_variant_name");
      sheet.getCell(r, 9).value = numField(deal, "b_quantity") || "";
      sheet.getCell(r, 10).value = numField(deal, "weight") || "";
      sheet.getCell(r, 11).value = strField(deal, "notes");

      sheet.getCell(r, 7).border = thinBorder();
      sheet.getCell(r, 7).font = { size: FONT_SIZE };
      sheet.getCell(r, 7).alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };

      const centerMiddle: Partial<ExcelJS.Alignment> = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      for (const c of [8, 9, 10]) {
        const cell = sheet.getCell(r, c);
        cell.border = thinBorder();
        cell.font = { size: FONT_SIZE };
        cell.alignment = centerMiddle;
      }

      const nCell = sheet.getCell(r, 11);
      nCell.border = thinBorder();
      nCell.font = { color: { argb: "FF770000" }, bold: true, size: FONT_SIZE };
      nCell.alignment = { vertical: "top", wrapText: true };

      const productName = strField(deal, "product_name");
      const notes = strField(deal, "notes");
      const heightCells: Array<{ text: string; colWidth: number }> = [
        { text: productName, colWidth: COLW_PRODUCT },
        { text: notes, colWidth: COLW_NOTE },
        { text: strField(deal, "product_variant_name"), colWidth: COLW_REST },
      ];
      if (firstAddr) {
        heightCells.push(
          { text: strField(deal, "client_name"), colWidth: COLW_MAIN },
          { text: strField(deal, "point_address"), colWidth: COLW_MAIN },
          { text: strField(deal, "point_comment"), colWidth: COLW_MAIN },
        );
      }
      const rowHeight = estimateRowHeightPt({
        cells: heightCells,
        fontSize: FONT_SIZE,
        minHeight: ROW_HEIGHT_DATA,
      });
      sheet.getRow(r).height = rowHeight;
      if (process.env.NODE_ENV === "development") {
        console.debug("[routingSheetExcel] deal row height", {
          row: r,
          rowHeight,
          notesLen: notes.length,
          productLen: productName.length,
          firstAddr,
        });
      }
      r += 1;
    }
  }

  const sumDoc = dealsSum && dealsSum[0];
  if (sumDoc) {
    for (let c = 1; c <= 6; c++) {
      const cell = sheet.getCell(r, c);
      cell.value = "";
      cell.border = thinBorder();
      cell.font = { size: FONT_SIZE };
    }
    sheet.getCell(r, 7).value = "Итого";
    sheet.getCell(r, 7).font = { bold: true, size: FONT_SIZE };
    sheet.getCell(r, 9).value = numField(sumDoc, "total_quantity") || "";
    sheet.getCell(r, 10).value = numField(sumDoc, "total_weight") || "";
    sheet.getCell(r, 9).alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    sheet.getCell(r, 10).alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    sheet.getCell(r, 9).font = { size: FONT_SIZE };
    sheet.getCell(r, 10).font = { size: FONT_SIZE };
    for (let c = 1; c <= COL_LAST; c++) {
      sheet.getCell(r, c).border = thinBorder();
      if (c !== 7 && c !== 9 && c !== 10) {
        sheet.getCell(r, c).font = { size: FONT_SIZE };
      }
    }
    sheet.getRow(r).height = ROW_HEIGHT_DATA;
    r += 1;
  }

  return r;
}

/** A — пусто (ручной ввод); B — Место; C–G — данные. */
const WH_COL_FIRST = 2;
const WH_COL_LAST = 7;

/**
 * Пишет блок «продукты со склада».
 * @param headerRow — первая строка блока: персиковая шапка (дата, направление, комплектовщик);
 *   строка заголовков колонок = headerRow + 1, данные с headerRow + 2.
 */
function writeWarehouseProductBlock(
  sheet: ExcelJS.Worksheet,
  headerRow: number,
  productsRaw: any[],
): { nextRow: number; totalBuckets: number } {
  const products = productGroup(productsRaw || []);
  const c0 = WH_COL_FIRST;
  const infoRow = headerRow;
  const colHeaderRow = headerRow + 1;

  sheet.mergeCells(infoRow, c0, infoRow, c0 + 1);
  sheet.getCell(infoRow, c0).value = "Дата и время сбора ________";
  sheet.getCell(infoRow, c0).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(infoRow, c0).fill = WH_FILL_INFO_ROW;
  sheet.getCell(infoRow, c0).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  sheet.getCell(infoRow, c0).border = {
    top: BORDER_BLACK_THICK,
    left: BORDER_BLACK_THICK,
    bottom: BORDER_BLACK_THIN,
    right: BORDER_BLACK_THIN,
  };

  sheet.mergeCells(infoRow, c0 + 2, infoRow, c0 + 3);
  sheet.getCell(infoRow, c0 + 2).value = "Направление ________";
  sheet.getCell(infoRow, c0 + 2).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(infoRow, c0 + 2).fill = WH_FILL_INFO_ROW;
  sheet.getCell(infoRow, c0 + 2).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  sheet.getCell(infoRow, c0 + 2).border = {
    top: BORDER_BLACK_THICK,
    left: BORDER_BLACK_THIN,
    bottom: BORDER_BLACK_THIN,
    right: BORDER_BLACK_THIN,
  };

  sheet.mergeCells(infoRow, c0 + 4, infoRow, c0 + 5);
  sheet.getCell(infoRow, c0 + 4).value = "Комплектовщик ________";
  sheet.getCell(infoRow, c0 + 4).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(infoRow, c0 + 4).fill = WH_FILL_INFO_ROW;
  sheet.getCell(infoRow, c0 + 4).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  sheet.getCell(infoRow, c0 + 4).border = {
    top: BORDER_BLACK_THICK,
    left: BORDER_BLACK_THIN,
    bottom: BORDER_BLACK_THIN,
    right: BORDER_BLACK_THICK,
  };
  sheet.getRow(infoRow).height = ROW_HEIGHT_WH_INFO;

  const hdr = [
    "Место",
    "Продукт",
    "Тара",
    "Всего, шт",
    "Всего, кг",
    "ПРИМЕЧАНИЕ",
  ];
  hdr.forEach((h, i) => {
    const cell = sheet.getCell(colHeaderRow, c0 + i);
    cell.value = h;
    cell.font = { bold: true, size: FONT_SIZE_HEADER };
    const isNote = i === hdr.length - 1;
    cell.fill = isNote ? WH_FILL_INFO_ROW : WH_FILL_COL_HEADER_GREEN;
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    const isFirst = i === 0;
    const isLast = i === hdr.length - 1;
    cell.border = {
      top: BORDER_BLACK_THIN,
      bottom: BORDER_BLACK_THICK,
      left: isFirst ? BORDER_BLACK_THICK : BORDER_BLACK_THIN,
      right: isLast ? BORDER_BLACK_THICK : BORDER_BLACK_THIN,
    };
  });
  sheet.getRow(colHeaderRow).height = ROW_HEIGHT_TABLE_HEADER;

  let r = colHeaderRow + 1;
  let totalBuckets = 0;

  for (const product of products) {
    const containers = product.containers || [];
    for (const c of containers) {
      sheet.getCell(r, c0).value = "";
      sheet.getCell(r, c0 + 1).value = strField(c, "product_name");
      sheet.getCell(r, c0 + 2).value = strField(c, "container_name");
      const cnt = numField(c, "container_count");
      const w = numField(c, "container_weight");
      sheet.getCell(r, c0 + 3).value = cnt || "";
      sheet.getCell(r, c0 + 4).value = w || "";
      sheet.getCell(r, c0 + 5).value = "";
      totalBuckets += cnt;
      for (let col = c0; col <= WH_COL_LAST; col++) {
        const cell = sheet.getCell(r, col);
        cell.border = thinBorder();
        cell.font = { size: FONT_SIZE };
        if (col === c0 + 1) {
          cell.alignment = { vertical: "top", wrapText: true };
        } else if (col >= c0 + 2 && col <= c0 + 4) {
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
        } else {
          cell.alignment = { vertical: "top", wrapText: true };
        }
      }
      sheet.getRow(r).height = estimateRowHeightPt({
        cells: [
          { text: strField(c, "product_name"), colWidth: COLW_WH_NAME },
          { text: strField(c, "container_name"), colWidth: COLW_REST },
        ],
        fontSize: FONT_SIZE,
        minHeight: ROW_HEIGHT_DATA,
      });
      r += 1;
    }
  }

  const totalRow = r;
  sheet.getCell(totalRow, c0 + 1).value = "Итого ведер (авто):";
  sheet.getCell(totalRow, c0 + 1).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(totalRow, c0 + 3).value = totalBuckets;
  sheet.getCell(totalRow, c0 + 3).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(totalRow, c0 + 3).alignment = {
    horizontal: "center",
    vertical: "middle",
  };

  const placesCell = sheet.getCell(totalRow, WH_COL_LAST);
  placesCell.value = "Количество мест __________";
  placesCell.font = { bold: true, size: FONT_SIZE };
  placesCell.fill = WH_FILL_INFO_ROW;
  placesCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    indent: 1,
    wrapText: true,
  };

  for (let col = c0; col <= WH_COL_LAST; col++) {
    const cell = sheet.getCell(totalRow, col);
    clearCellBorders(cell);
    if (col !== c0 + 1 && col !== c0 + 3 && col !== WH_COL_LAST) {
      cell.value = "";
      cell.font = { size: FONT_SIZE };
    }
  }

  sheet.getRow(totalRow).height = Math.max(ROW_HEIGHT_DATA, ROW_HEIGHT_WH_INFO);
  r += 1;

  if (process.env.NODE_ENV === "development") {
    console.debug("[FIX:routingSheetExcel] warehouse block footer", {
      totalBuckets,
      endRow: r,
    });
  }

  return { nextRow: r, totalBuckets };
}

/** Ширины столбцов 1–7 — max(заявки, склад, шапка D/E), блоки на одном листе делят колонки. */
function applyCombinedColumnWidths(sheet: ExcelJS.Worksheet) {
  sheet.getColumn(1).width = Math.max(COLW_PAIR, COLW_WH_INDEX);
  sheet.getColumn(2).width = Math.max(COLW_PAIR, COLW_WH_CODE);
  sheet.getColumn(3).width = Math.max(COLW_NUM, COLW_WH_NAME);
  sheet.getColumn(4).width = Math.max(
    COLW_MAIN,
    COLW_WH_PLACE,
    COLW_META_LABEL,
  );
  sheet.getColumn(5).width = Math.max(COLW_MAIN, COLW_WH_QTY, COLW_META_VALUE);
  sheet.getColumn(6).width = Math.max(COLW_MAIN, COLW_WH_QTY);
  sheet.getColumn(7).width = Math.max(COLW_PRODUCT, COLW_WH_NOTE);
  for (let c = 8; c <= COL_LAST; c++) {
    sheet.getColumn(c).width = c === 11 ? COLW_NOTE : COLW_REST;
  }
}

export async function buildRoutingSheetWorkbookBuffer(
  payload: RoutingSheetBatchPayload,
): Promise<ArrayBuffer> {
  debug("batch keys", Object.keys(payload || {}));

  const di =
    (payload.delivery_instance && payload.delivery_instance.documents) || [];
  const deals = (payload.deals && payload.deals.documents) || [];
  const dealsSum = (payload.deals_sum && payload.deals_sum.documents) || [];
  const products = (payload.products && payload.products.documents) || [];

  const diRet =
    (payload.delivery_instance_return &&
      payload.delivery_instance_return.documents) ||
    [];
  const dealsRet =
    (payload.deals_return && payload.deals_return.documents) || [];
  const dealsSumRet =
    (payload.deals_sum_return && payload.deals_sum_return.documents) || [];
  const productsRet =
    (payload.products_return && payload.products_return.documents) || [];

  const wb = new ExcelJS.Workbook();
  wb.created = new Date();

  const sheet = wb.addWorksheet(SHEET_ROUTING, {
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    },
  });
  sheet.properties.defaultRowHeight = ROW_HEIGHT_DATA;

  const meta = di[0] || {};
  sheet.getCell(1, META_LABEL_COL).value = "Водитель";
  sheet.getCell(1, META_LABEL_COL).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(1, META_VALUE_COL).value = strField(meta, "driver_name");
  sheet.getCell(1, META_VALUE_COL).font = { size: FONT_SIZE };
  sheet.getRow(1).height = ROW_HEIGHT_META;

  sheet.getCell(2, META_LABEL_COL).value = "Дата доставки";
  sheet.getCell(2, META_LABEL_COL).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(2, META_VALUE_COL).value = formatDeliveryDate(
    coerceField(meta, "delivery_date"),
  );
  sheet.getCell(2, META_VALUE_COL).font = { size: FONT_SIZE };
  sheet.getRow(2).height = ROW_HEIGHT_META;

  sheet.getCell(3, META_LABEL_COL).value = "Время погрузки";
  sheet.getCell(3, META_LABEL_COL).font = { bold: true, size: FONT_SIZE };
  sheet.getCell(3, META_VALUE_COL).value = strField(meta, "loading_time");
  sheet.getCell(3, META_VALUE_COL).font = { size: FONT_SIZE };
  sheet.getRow(3).height = ROW_HEIGHT_META;

  if (process.env.NODE_ENV === "development") {
    console.debug(
      "[FIX:routingSheetExcel] single sheet: meta D/E, gap before warehouse",
      {
        ROW_HEIGHT_META,
        ROW_HEIGHT_DATA,
        ROW_GAP_BEFORE_WAREHOUSE,
        COLW_META_LABEL,
        COLW_META_VALUE,
      },
    );
  }

  const clientMap = new Map<string, number>();
  const nextNo = { n: 1 };
  let r = 5;
  debug("deals table start row", r, "deals", deals.length);

  r = fillDealsTable(sheet, r, deals, dealsSum, clientMap, nextNo);

  if (dealsRet.length > 0 || diRet.length > 0) {
    r += 1;
    sheet.mergeCells(r, 1, r, COL_LAST);
    sheet.getCell(r, 1).value = "Возвраты";
    sheet.getCell(r, 1).font = { bold: true, size: FONT_SIZE };
    sheet.getCell(r, 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEEEEEE" },
    };
    sheet.getRow(r).height = ROW_HEIGHT_TABLE_HEADER;
    r += 1;

    const m = diRet[0] || meta;
    sheet.getCell(r, META_LABEL_COL).value = "Водитель";
    sheet.getCell(r, META_LABEL_COL).font = { bold: true, size: FONT_SIZE };
    sheet.getCell(r, META_VALUE_COL).value = strField(m, "driver_name");
    sheet.getCell(r, META_VALUE_COL).font = { size: FONT_SIZE };
    sheet.getRow(r).height = ROW_HEIGHT_META;
    r += 1;
    sheet.getCell(r, META_LABEL_COL).value = "Дата доставки";
    sheet.getCell(r, META_LABEL_COL).font = { bold: true, size: FONT_SIZE };
    sheet.getCell(r, META_VALUE_COL).value = formatDeliveryDate(
      coerceField(m, "delivery_date"),
    );
    sheet.getCell(r, META_VALUE_COL).font = { size: FONT_SIZE };
    sheet.getRow(r).height = ROW_HEIGHT_META;
    r += 1;
    sheet.getCell(r, META_LABEL_COL).value = "Время погрузки";
    sheet.getCell(r, META_LABEL_COL).font = { bold: true, size: FONT_SIZE };
    sheet.getCell(r, META_VALUE_COL).value = strField(m, "loading_time");
    sheet.getCell(r, META_VALUE_COL).font = { size: FONT_SIZE };
    sheet.getRow(r).height = ROW_HEIGHT_META;
    r += 2;

    r = fillDealsTable(sheet, r, dealsRet, dealsSumRet, new Map(), { n: 1 });
  }

  for (let i = 0; i < ROW_GAP_BEFORE_WAREHOUSE; i++) {
    sheet.getRow(r + i).height = ROW_HEIGHT_DATA;
  }
  const whHeaderRow = r + ROW_GAP_BEFORE_WAREHOUSE;
  if (process.env.NODE_ENV === "development") {
    console.debug("[FIX:routingSheetExcel] warehouse block after gap", {
      gapStartRow: r,
      whHeaderRow,
    });
  }

  let wr = writeWarehouseProductBlock(sheet, whHeaderRow, products).nextRow;
  if (productsRet.length) {
    wr += 1;
    sheet.mergeCells(wr, WH_COL_FIRST, wr, WH_COL_LAST);
    sheet.getCell(wr, 2).value = "Возвраты (продукты)";
    sheet.getCell(wr, 2).font = { bold: true };
    wr += 2;
    writeWarehouseProductBlock(sheet, wr, productsRet);
  }

  applyCombinedColumnWidths(sheet);

  const buf = await wb.xlsx.writeBuffer();
  debug("workbook bytes", buf.byteLength);
  return buf as ArrayBuffer;
}
